import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import merge from 'lodash/merge';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import Portal from './Portal';
import { resources } from '../utils/locales';
import { getQueryParamFromLocation } from './utils';
import { GlobalContextProvider } from './state/GlobalContext';
import ContextComposer from './state/ContextComposer';
import customTheme from '../chakra';
import SentryErrorBoundary from '../components/SentryErrorBoundary/SentryErrorBoundary';
import '../utils/awtomicLogo';
import { MOCK_AUTH_TOKEN, MOCK_CUSTOMER_ID } from './constants';

import '../styles/subsPortal.scss';

const theme = extendTheme(customTheme);

const getCountries = (): Country[] =>
  [
    ...document.querySelector<HTMLFormElement>('#bundleappCountrySelect')
      ?.options,
  ].map((op) => ({
    name: op.value,
    provinces: JSON.parse(op.dataset.provinces),
  }));

document.addEventListener('DOMContentLoaded', () => {
  const localTranslations = window?.bundleapp?.settings?.translations;
  i18n.use(initReactI18next).init({
    resources: merge(resources, localTranslations),
    lng: window?.Shopify?.locale || document.documentElement.lang || 'en',
    fallbackLng: 'en',

    interpolation: {
      escapeValue: false,
    },
  });

  const $root = document.getElementById('bundleapp-subscriptions-root');

  if ($root) {
    $root.className = 'bundleapp-subscription-root--loaded';

    const props = window['reactInit'] ? window['reactInit'] : {};
    const subscriptionId = getQueryParamFromLocation('subscriptionId');
    const isDemo = getQueryParamFromLocation('demo');
    const isMockSubscription = subscriptionId?.includes('TEST') && !!isDemo;
    const mockCredentials = {
      authToken: MOCK_AUTH_TOKEN,
      customerId: MOCK_CUSTOMER_ID,
    };

    props['countries'] = getCountries();
    props['subscriptionId'] = subscriptionId;

    const root = createRoot($root);
    root.render(
      <StrictMode>
        <SentryErrorBoundary>
          <ChakraProvider theme={theme}>
            <GlobalContextProvider
              value={{
                ...props,
                ...(isMockSubscription ? mockCredentials : {}),
              }}
            >
              <ContextComposer>
                <Portal />
              </ContextComposer>
            </GlobalContextProvider>
          </ChakraProvider>
        </SentryErrorBoundary>
      </StrictMode>,
    );
  } else {
    // store the token and redirect to the account login
    const shopifyRoot = window.Shopify?.routes?.root || '/';

    window.location.href = `${shopifyRoot}account/login?checkout_url=${window.location.href}`;
  }
});
